<template>
  <div class="home-map">
    <p class="home-text-1">Poszukujesz sprawdzonego wykonawcę ociepleń w swojej okolicy? Zapraszamy do skorzystania z wyszukiwarki Certyfikowanych Wykonawców ROCKWOOL.</p>
    <p class="home-text-2">W łatwy i szybki sposób znajdziesz dobrego wykonawcę specjalizującego się w ociepleniach domów jednorodzinnych, a w szczególności w ocieplaniu ścian zewnętrznych, poddaszy. Certyfikowani Wykonawcy ROCKWOOL to kompetentni i doświadczeni wykonawcy ociepleń z wełny skalnej.</p>
    <template v-if="!loading">
        <div class="home-map--input-wrapper">
          <div class="home-map--input-wrapper--inner">
            <GMapAutocomplete
                placeholder="Wpisz lokalizację"
                @place_changed="setPlace"
            />
            <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
          </div>
          <div class="home-map--input-wrapper--certificates">
            <select v-model="selectedCertificate" id="certificateSelect"  @change="handleSelectChange">
              <option v-for="(certificate, index) in uniqueCertificates" :key="index" :value="certificate">
                {{ certificate }}
              </option>
            </select>
          </div>
        </div>
      <div class="home-map--inner-wrapper">
        <div class="home-map__aside">
          <perfect-scrollbar>
            <div class="home-map__list">
              <div class="home-map__list--item"
                   v-for="(item, index) in selectedPins"
                   :key="`map-item-${index}`"
                   :class="{active: currentContractor === item.id}"
                   @click="showContractorOnMap(item)"
              >

                <div class="home-map__list--item__body">
                  <img v-if="item.is_promoted" class="home-map__list--item--marker" loading="lazy" :src="marker">
                  <img v-if="!item.is_promoted" class="home-map__list--item--marker" loading="lazy" :src="markerGray">
                  <div class="home-map__list--item__body--person">
                    <span>{{ item.first_name }} {{ item.last_name }}</span>
                    <p>
                      {{ item.street }} {{ item.city }}<br />
                      {{ item.phone }} . {{ item.email }}
                    </p>
                  </div>
                </div>
                <div class="home-map__list--item__distance">
                  <span class="recommendation-label">{{ item.opinions.length }} {{ getRecommendationLabel(item.opinions.length) }}</span>
                  {{ item.distance }} km
                </div>
              </div>
            </div>
          </perfect-scrollbar>
        </div>
        <div class="home-map__map">
          <GMapMap
              :center="mapOptions.center"
              :zoom="7"
              :options="mapOptions.googleMapsOptions"
              map-type-id="terrain"
              :max-zoom="7"
              style="width: 100%; height: 400px"
          >
            <GMapMarker
                :position="defaultPosition"
                :icon= '{
                    url: require("@/assets/images/user-location.svg"),
                    scaledSize: {width: 30, height: 30}
                }'
                :clickable="false"
                :draggable="false"
            />
            <GMapMarker
                :key="index"
                :icon= '{
                    url: m.is_promoted ? require("@/assets/images/marker.svg") : require("@/assets/images/marker-gray.svg"),
                    scaledSize: {width: 30, height: 30}
                }'
                v-for="(m, index) in selectedPins"
                :position="getPosition(m)"
                :clickable="true"
                @click="currentContractor = m.id"
                :draggable="false"
            >
              <GMapInfoWindow v-if="currentContractor === m.id || currentHover === m.id">
                <div class="home-map__map--info" @click="goToContractorPage(m.id)">
                  {{ m.first_name }} {{ m.last_name }}
                  <font-awesome-icon icon="fa-solid fa-chevron-right" />
                </div>
              </GMapInfoWindow>
            </GMapMarker>
          </GMapMap>
        </div>
      </div>
    </template>
    <spinner v-else />
  </div>
</template>
<script>
import {defineComponent} from 'vue'
import Spinner from '@/components/Spinner'
import {mapGetters} from 'vuex'
import distance from '@turf/distance'
import { point } from '@turf/helpers'
import marker from '@/assets/images/marker.svg'
import markerGray from '@/assets/images/marker-gray.svg'

const CENTER_CORD = {lat: 51.9189046, lng: 19.1343786}

export default defineComponent({
  name: 'HomeMap',
  components: {
    Spinner
  },
  watch: {
    contractors (newContractors) {
      this.pins = newContractors;
      this.pins.forEach(pin => {
        pin.distance = this.getDistance(pin);
      })
      if (this.pins.length === 0) {
        return []
      }

      this.setSelectedPins();

    }
  },
  computed: {
    ...mapGetters({
      contractors: 'contractors/getContractorsList'
    })
  },
  data() {
    return {
      loading: true,
      defaultPosition: CENTER_CORD,
      currentContractor: null,
      currentHover: null,
      mapOptions: {
        center: CENTER_CORD,
        googleMapsOptions: {
          zoomControl: true,
          mapTypeControl: false,
          scaleControl: true,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false
        }
      },
      pins: [],
      uniqueCertificates:[],
      selectedCertificate: "",
      selectedPins: [],
      marker,
      markerGray
    }
  },
  mounted () {
    this.getUserLocation();
  },
  async created() {
    await this.$store.dispatch('contractors/GET_CONTRACTORS');
    this.loading = false
  },
  methods: {
    setSelectedPins() {
      const array = [...this.pins];
      this.pins=array.sort((a, b) => b.opinions.length - a.opinions.length)
      this.uniqueCertificates = [...new Set(this.pins.map(item => item.certificates).flat())];
      this.selectedCertificate = this.uniqueCertificates[0];
      this.selectedPins = this.pins.filter(pin => pin.certificates.includes(this.selectedCertificate));
    },
    getRecommendationLabel(count) {
      if (count === 1) return 'rekomendacja';
      if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) {
        return 'rekomendacje';
      }
      return 'rekomendacji';
    },
    handleSelectChange() {
      this.selectedPins = this.pins.filter(pin => pin.certificates.includes(this.selectedCertificate)).sort((a, b) => a.distance - b.distance);
    },
    getUserLocation () {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const cords = {lat: position.coords.latitude, lng: position.coords.longitude}
          this.mapOptions.center = cords
          this.defaultPosition = cords
        });
      }
    },
    showContractorOnMap (item) {
      this.mapOptions.center = { lat: parseFloat(item.latitude), lng: parseFloat(item.longitude) };
      this.currentContractor = item.id;
    },
    getPosition (item) {
      return {lat: parseFloat(item.latitude), lng: parseFloat(item.longitude)}
    },
    goToContractorPage (uuid) {
      this.$router.push({ name: 'Contractor', params: { uuid } })
    },
    setPlace (place) {
      if (place.name === '') {
        this.setSelectedPins();
      } else {
        this.defaultPosition.lat = place.geometry.location.lat();
        this.defaultPosition.lng = place.geometry.location.lng();

        this.mapOptions.center = { lat: this.defaultPosition.lat, lng: this.defaultPosition.lng };

        this.recalculateDistances();
        this.handleSelectChange();
      }
    },
    getDistance (item) {
      const from = point([this.defaultPosition.lat, this.defaultPosition.lng])
      const to = point([item.latitude, item.longitude])
      return distance(from, to, { units: 'kilometers' }).toFixed(2)
    },
    recalculateDistances () {
      this.pins.forEach(pin => {
        pin.distance = this.getDistance(pin);
      })
    }
  }
})
</script>
<style lang="scss" scoped>
@import "@/styles/global/_variables.scss";
h2{
  font-weight: 400;
  margin-bottom: 30px;
  text-align: justify;
  @media screen and (max-width: $breakpoint) {
    display: none;
  }
}
.home-text-1{
  font-size: 28px;
  text-align: unset;
  margin-bottom: 17px;
  @media screen and (max-width: $breakpoint){
    margin: 20px 10px;
    text-align: justify;
  }
}
.home-text-2{
  font-size: 14px;
  margin-bottom: 35px;
  @media screen and (max-width: $breakpoint){
    margin: 20px 10px;
    text-align: justify;
  }
}

.home-map{
  $asideWidth: 340px;
  padding-bottom: 40px;
  border-bottom: 1px solid #C9C9C9;
  &--inner-wrapper{
    display: flex;

    @media screen and (max-width: $breakpoint){
      flex-direction: column;
    }
  }

  &--input-wrapper{
    margin-bottom: 26px;
    position: relative;
    display: flex;
    @media screen and (max-width: $breakpoint){
      padding: 0 10px;
    }
    &--inner{
      border-bottom: 1px solid #000;
      width: $asideWidth;
      position: relative;
      @media screen and (max-width: $breakpoint){
        width: 100%;
      }
    }
    &--certificates{
      margin-left: 21px;
      border-bottom: 1px solid #000;
      width: $asideWidth;
      position: relative;
      @media screen and (max-width: $breakpoint){
        width: 100%;
      }
      select{
        height: 100%;
        width: 100%;
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
      }
      &::after {
        content: '\276F';
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%) rotate(-90deg);
        pointer-events: none;
        color: red;
        font-size: 16px;
      }
    }
    input{
      padding: 13px 16px 13px 0;
      width: 100%;
    }
    svg{
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      color: $color-main;
    }
  }
  &__aside{
    width: $asideWidth;
    margin-right: 20px;
    flex-shrink: 0;
    height: 400px;
    @media screen and (max-width: $breakpoint){
      order: 2;
      width: 100%;
      margin-right: 0;
    }
  }
  &__map{
    width: calc(100% - #{$asideWidth});
    @media screen and (max-width: $breakpoint){
      order: 1;
      width: 100%;
    }
    &--info{
      cursor: pointer;
      color: #fff;
      padding: 9px 12px;
      display: flex;
      align-items: center;
      svg{
        margin-left: 27px;
        width: 13px;
        height: 21px
      }
    }
  }
  &__list{
    &--item{
      padding: 18px 14px;
      border-bottom: 1px solid #C9C9C9;
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
      &--marker{
        height: 15px;
        width: 21px;
        margin-right: 10px;
      }
      &__body{
        display: flex;

        &--person > div {
          display: flex;
        }
      }
      &.active{
        background-color: #FCFCFC;
      }
      span{
        display: block;
        margin-bottom: 12px;
        line-height: 20px;
      }
      p{
        font-size: 12px;
        line-height: 16px;
        color: #C9C9C9;
      }
      &:first-of-type{
        border-top: 1px solid #C9C9C9;
      }
      &__distance{
        white-space: nowrap;
        color: #C9C9C9;
        display: flex;
        flex-direction: column;
        align-items: flex-end;

        .recommendation-label {
          white-space: nowrap;
          margin: 0;
        }
      }
    }
  }
  .ps {
    height: 100%;
  }
}
</style>
<style lang="scss">
@import "@/styles/global/_variables.scss";

.gm-style .gm-style-iw-c{
  background-color: $color-main !important;
  padding: 0 !important;
  border-radius: 0;
}
.gm-style .gm-style-iw-d{
  overflow: visible !important;
}
.gm-style .gm-style-iw-t::after{
  content: none;
}
.gm-style .gm-style-iw-a button{
  display: none !important;
}
</style>
